
/**
 * Custom hook for tracking GA4 events directly
 */
export const useAnalytics = () => {
  /**
   * Initialize GA4 analytics
   */
  const initializeAnalytics = () => {
    // Initialize dataLayer if it doesn't exist
    window.dataLayer = window.dataLayer || [];
    
    // Set gtag function
    window.gtag = function() { window.dataLayer.push(arguments); };
    
    console.log("GA4 analytics initialized");
    
    // Signal that analytics is initialized
    window.dataLayer.push({
      event: 'analytics_initialized',
      consent: 'basic',
      timestamp: new Date().toISOString()
    });

    // Ensure GA4 is properly initialized
    if (typeof window.gtag === 'function') {
      window.gtag('js', new Date());
      window.gtag('config', 'G-YYRPWSV3FY', {
        send_page_view: true,
        consent_mode: 'advanced'
      });
      console.log("GA4 initialized directly");
    }
  };

  /**
   * Track a page view
   */
  const trackPageView = (title: string, path: string, referrer?: string) => {
    if (typeof window.gtag !== 'function') {
      console.warn("GA4 gtag not available for page view tracking");
      return;
    }

    const params: Record<string, any> = {
      page_title: title,
      page_location: window.location.href,
      page_path: path
    };
    
    if (referrer) {
      params.page_referrer = referrer;
    }
    
    window.gtag('event', 'page_view', params);
    console.log("GA4 page view tracked:", params);
  };

  /**
   * Track a custom event
   */
  const trackEvent = (eventName: string, eventParams?: Record<string, any>) => {
    if (typeof window.gtag !== 'function') {
      console.warn("GA4 gtag not available for event tracking");
      return;
    }

    const params = {
      event_timestamp: new Date().toISOString(),
      ...eventParams
    };
    
    window.gtag('event', eventName, params);
    console.log("GA4 event tracked:", eventName, params);
  };

  /**
   * Track form submission
   */
  const trackFormSubmission = (formType: string, formData: Record<string, any>) => {
    const eventParams: Record<string, any> = {
      form_type: formType
    };
    
    // Add relevant form data without PII
    if (formData.loanType) eventParams.loan_type = formData.loanType;
    if (formData.loanAmount) eventParams.loan_amount = formData.loanAmount;
    if (formData.loanTerm) eventParams.loan_term = formData.loanTerm;
    
    trackEvent('form_submission', eventParams);
  };

  /**
   * Track loan application funnel steps
   */
  const trackLoanApplicationStep = (step: string, loanInfo?: Record<string, any>, referrer?: string) => {
    const eventParams: Record<string, any> = {
      step: step
    };
    
    if (loanInfo) {
      if (loanInfo.loanType) eventParams.loan_type = loanInfo.loanType;
      if (loanInfo.loanAmount) eventParams.loan_amount = loanInfo.loanAmount;
      if (loanInfo.loanTerm) eventParams.loan_term = loanInfo.loanTerm;
    }
    
    if (referrer) {
      eventParams.referrer = referrer;
    }
    
    trackEvent('loan_application_step', eventParams);
  };

  /**
   * Track navigation to loan application page
   */
  const trackNavigationToApplication = (source: string, loanInfo?: Record<string, any>) => {
    const eventParams: Record<string, any> = {
      source: source
    };
    
    if (loanInfo) {
      if (loanInfo.loanType) eventParams.loan_type = loanInfo.loanType;
      if (loanInfo.loanAmount) eventParams.loan_amount = loanInfo.loanAmount;
      if (loanInfo.loanTerm) eventParams.loan_term = loanInfo.loanTerm;
    }
    
    trackEvent('navigate_to_loan_application', eventParams);
  };

  return {
    initializeAnalytics,
    trackPageView,
    trackEvent,
    trackFormSubmission,
    trackLoanApplicationStep,
    trackNavigationToApplication
  };
};
